import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { ToggleButton } from "primereact/togglebutton";
import { Divider } from "primereact/divider";
import { Chart } from "primereact/chart";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dropdown } from "primereact/dropdown";
import axiosInstance from "../services/axiosInstance";
import { toYmd } from '../utils/dateOnly';

// ==========================================================
// ACCOUNTING DASHBOARD — VISUALLY POLISHED VERSION
// ==========================================================

export default function AccountingDashboard() {
  const today = new Date();

  const [asAtDate, setAsAtDate] = useState(today);
  const [range, setRange] = useState([today, today]);
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [debouncedRange, setDebouncedRange] = useState(range);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [customerDues, setCustomerDues] = useState([]);
  const [supplierDues, setSupplierDues] = useState([]);
  const [showAccountant, setShowAccountant] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingDues, setLoadingDues] = useState(false);
  const [error, setError] = useState(null);

  // Debounce date range
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedRange(range), 500);
    return () => clearTimeout(handler);
  }, [range]);

  // ------------------------------
  // FETCH BALANCE SHEET (AS AT)
  // ------------------------------
  useEffect(() => {
    const fetchBalanceSheet = async () => {
      setLoadingBalance(true);
      try {
        const deptParam = selectedDept ? `&department=${selectedDept}` : "";
            const res = await axiosInstance.get(
            `/api/balance-sheet?as_at=${toYmd(asAtDate)}${deptParam}`
        );
        setBalanceSheet(res.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching balance sheet:", err);
        setError("Failed to load balance sheet");
      } finally {
        setLoadingBalance(false);
      }
    };
    fetchBalanceSheet();
  }, [asAtDate, selectedDept]);

  // ------------------------------
  // FETCH SUMMARY STATS (PROFIT)
  // ------------------------------
  useEffect(() => {
    const fetchSummary = async () => {
      if (!debouncedRange || debouncedRange.length !== 2) return;
      setLoadingSummary(true);
      try {
        const from = toYmd(debouncedRange[0]);
        const to = toYmd(debouncedRange[1]);
        const deptParam = selectedDept ? `&department=${selectedDept}` : "";
        const res = await axiosInstance.get(
            `/api/summary-stats?start_date=${from}&end_date=${to}${deptParam}`
        );
        setSummary(res.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching summary stats:", err);
        setError("Failed to load summary stats");
      } finally {
        setLoadingSummary(false);
      }
    };
    fetchSummary();
  }, [debouncedRange, selectedDept]);

  // ------------------------------
  // FETCH DUES LISTS
  // ------------------------------
  useEffect(() => {
    const fetchDues = async () => {
      setLoadingDues(true);
      const deptParam = selectedDept ? `?department=${selectedDept}` : "";
      try {
        const [cRes, sRes] = await Promise.all([
          axiosInstance.get(`/api/customer-dues${deptParam}`),
          axiosInstance.get(`/api/supplier-dues${deptParam}`),
        ]);
        setCustomerDues(cRes.data || []);
        setSupplierDues(sRes.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching dues lists:", err);
        setError("Failed to load dues lists");
      } finally {
        setLoadingDues(false);
      }
    };
    fetchDues();
  }, [selectedDept]);

  useEffect(() => {
    axiosInstance.get("/api/departments")
        .then(res => setDepartments(res.data || []))
        .catch(err => console.error("Dept load failed", err));
  }, []);

  // ----- Stylized KPI Card -----
  const kpiCard = (title, value, sub, accent = "#3B82F6") => (
    <Card
      className="p-3 shadow-3 border-round-xl h-full"
      style={{ borderTop: `4px solid ${accent}` }}
    >
      <div className="text-600 mb-1 text-sm">{title}</div>
      <div className="text-2xl font-bold">Rs. {value?.toLocaleString() || "0.00"}</div>
      {sub && <div className="text-sm text-600 mt-1">{sub}</div>}
    </Card>
  );

  // ------------------------------
  // CHART DATA — GREEN PROFIT VISUAL
  // ------------------------------
  const profitChart = summary?.profit_summary
    ? {
        labels: ["Collections (Cash + Card)", "Gross Profit"],
        datasets: [
          {
            label: [ "Rs."],
            backgroundColor: ["#58a7ac7a", "#15ee6583"], 
            borderColor: ["#5e64bb", "#0dac47"],
            borderWidth: 1,
            data: [
              (summary.total_payments || []).reduce(
                (a, b) => a + (b.total_received || 0),
                0
              ),
              summary.profit_summary.gross_profit || 0,
            ],
          },
        ],
      }
    : null;

  return (
    <div className="p-4">
      <div className="flex justify-content-between align-items-center mb-3">
        <h2 className="text-2xl font-semibold">Accounting Dashboard</h2>
        <Dropdown
            value={selectedDept}
            options={[
                { id: null, department: "All Departments" },
                ...departments
            ]}
            optionLabel="department"
            optionValue="id"
            onChange={(e) => setSelectedDept(e.value)}
            placeholder="Filter by Department"
            className="w-20rem"
        />
        <span className="text-600">As at: {asAtDate.toLocaleDateString()}</span>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      {/* ================= KPI ROW ================= */}
      <div className="grid">
        <div className="md:col-3 sm:col-12">
          {loadingBalance ? (
            <ProgressSpinner />
          ) : (
            kpiCard(
              "Cash in Hand",
              balanceSheet?.assets?.cash_in_hand,
              "As at selected date",
              "#22C55E" // green
            )
          )}
        </div>
        <div className="md:col-3 sm:col-12">
          {loadingBalance ? (
            <ProgressSpinner />
          ) : (
            kpiCard(
              "Stock Value",
              balanceSheet?.assets?.stock_value,
              "Inventory at cost",
              "#3B82F6" // blue
            )
          )}
        </div>
        <div className="md:col-3 sm:col-12">
          {loadingBalance ? (
            <ProgressSpinner />
          ) : (
            kpiCard(
              "Customers Owe You",
              balanceSheet?.assets?.customer_dues,
              "Outstanding receivables",
              "#F59E0B" // amber
            )
          )}
        </div>
        <div className="md:col-3 sm:col-12">
          {loadingBalance ? (
            <ProgressSpinner />
          ) : (
            kpiCard(
              "You Owe Suppliers",
              balanceSheet?.liabilities?.supplier_dues,
              "Outstanding payables",
              "#EF4444" // red
            )
          )}
        </div>
      </div>

      {/* ================= PROFIT SUMMARY ================= */}
      <Card className="mb-4 shadow-2 border-round-xl">
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="text-xl m-0">Profit Summary</h3>
          <Calendar
            value={range}
            onChange={(e) => setRange(e.value)}
            selectionMode="range"
            showIcon
            placeholder="Select date range"
          />
        </div>

        {loadingSummary && (
          <div className="flex justify-center py-3">
            <ProgressSpinner />
          </div>
        )}

        {summary && summary.profit_summary && !loadingSummary && (
          <div className="grid">
            <div className="col-12 md:col-6">
              <div className="mb-2 text-600">Gross Profit</div>
              <div className="text-3xl font-bold text-green-600">
                Rs. {summary.profit_summary.gross_profit?.toLocaleString()}
              </div>
              <div className="text-sm text-600 mb-3">
                Margin: {summary.profit_summary.margin_percent}%
              </div>

              <Divider />

              <div className="mb-2 text-600">Units Sold (context)</div>
              <div className="text-xl font-bold">
                {summary.total_sales?.toLocaleString()}
              </div>
            </div>
            <div className="col-12 md:col-6">
              {profitChart && <Chart type="bar" data={profitChart} options={{ maintainAspectRatio: false, aspectRatio: 0.8}}/>}
            </div>
          </div>
        )}
      </Card>

      {/* ================= MONEY POSITION (BALANCE SHEET) ================= */}
      <Card className="mb-4 shadow-2 border-round-xl">
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="text-xl m-0">Money Position (Balance Sheet)</h3>
          <Calendar
            value={asAtDate}
            onChange={(e) => setAsAtDate(e.value)}
            showIcon
            placeholder="As at date"
          />
        </div>

        {loadingBalance ? (
          <div className="flex justify-center py-3">
            <ProgressSpinner />
          </div>
        ) : (
          balanceSheet && (
            <div className="grid">
              <div className="col-12 md:col-6">
                <h4 className="text-lg mb-2">Assets (What you own)</h4>
                <ul className="list-none p-0">
                  <li className="flex justify-content-between mb-2">
                    <span>Cash in Hand</span>
                    <span>
                      Rs. {balanceSheet.assets.cash_in_hand?.toLocaleString()}
                    </span>
                  </li>
                  <li className="flex justify-content-between mb-2">
                    <span>Card Receivable</span>
                    <span>
                      Rs. {balanceSheet.assets.card_receivable?.toLocaleString()}
                    </span>
                  </li>
                  <li className="flex justify-content-between mb-2">
                    <span>Customers Owe You</span>
                    <span>
                      Rs. {balanceSheet.assets.customer_dues?.toLocaleString()}
                    </span>
                  </li>
                  <li className="flex justify-content-between mb-2">
                    <span>Stock Value</span>
                    <span>
                      Rs. {balanceSheet.assets.stock_value?.toLocaleString()}
                    </span>
                  </li>
                  <Divider />
                  <li className="flex justify-content-between font-bold">
                    <span>Total Assets</span>
                    <span>
                      Rs. {balanceSheet.assets.total_assets?.toLocaleString()}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="col-12 md:col-6">
                <h4 className="text-lg mb-2">Liabilities (What you owe)</h4>
                <ul className="list-none p-0">
                  <li className="flex justify-content-between mb-2">
                    <span>Suppliers You Owe</span>
                    <span>
                      Rs. {balanceSheet.liabilities.supplier_dues?.toLocaleString()}
                    </span>
                  </li>
                  <Divider />
                  <li className="flex justify-content-between font-bold">
                    <span>Total Liabilities</span>
                    <span>
                      Rs. {balanceSheet.liabilities.total_liabilities?.toLocaleString()}
                    </span>
                  </li>
                </ul>

                <Divider />
                <div className="flex justify-content-between text-xl font-bold text-green-700">
                  <span>Your Business Value</span>
                  <span>
                    Rs. {balanceSheet.equity.business_value?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )
        )}
      </Card>

      {/* ================= OUTSTANDING LISTS ================= */}
      <div className="grid mb-4">
        <div className="col-12 md:col-6">
          <Card className="shadow-2 border-round-xl">
            <h3 className="text-xl mb-3">Customers with Dues</h3>
            {loadingDues ? (
              <ProgressSpinner />
            ) : (
              <DataTable value={customerDues} emptyMessage="No customer dues" paginator rows={5}>
                <Column field="customer_name" header="Customer" />
                <Column field="amount_due" header="Amount (Rs.)" />
                <Column field="days_pending" header="Days Pending" />
              </DataTable>
            )}
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card className="shadow-2 border-round-xl">
            <h3 className="text-xl mb-3">Suppliers with Dues</h3>
            {loadingDues ? (
              <ProgressSpinner />
            ) : (
              <DataTable value={supplierDues} emptyMessage="No supplier dues" paginator rows={5}>
                <Column field="supplier_name" header="Supplier" />
                <Column field="amount_due" header="Amount (Rs.)" />
                <Column field="due_in_days" header="Due In (Days)" />
              </DataTable>
            )}
          </Card>
        </div>
      </div>

      {/* ================= ACCOUNTANT VIEW (OPTIONAL) ================= */}
      <Card className="shadow-2 border-round-xl">
        <div className="flex justify-content-between align-items-center mb-3">
          <h3 className="text-xl m-0">Audit Ledger View</h3>
          <ToggleButton
            checked={showAccountant}
            onChange={(e) => setShowAccountant(e.value)}
            onLabel="Hide details"
            offLabel="Show detailed accounts"
          />
        </div>

        {showAccountant && balanceSheet && (
          <DataTable
            value={[
              { account: "Cash (1000)", balance: balanceSheet.assets.cash_in_hand },
              { account: "Card (1010)", balance: balanceSheet.assets.card_receivable },
              { account: "AR (1100)", balance: balanceSheet.assets.customer_dues },
              { account: "Inventory (5000)", balance: balanceSheet.assets.stock_value },
              { account: "AP (2000)", balance: balanceSheet.liabilities.supplier_dues },
            ]}
            emptyMessage="No ledger data"
          >
            <Column field="account" header="Account" />
            <Column field="balance" header="Balance (Rs.)" />
          </DataTable>
        )}
      </Card>
    </div>
  );
}
